import React, { useEffect, useState } from "react";
import { isEqual, pick } from "underscore";
import { connect } from "react-redux";
import { compose } from "redux";
import { withApollo } from "react-apollo";
import { API, Auth } from "aws-amplify";
import {
  loadActionStatus as loadActionStatusAction,
  loadLastFilter,
  loadUserInfo as loadUserInfoAction,
  updateLastDisplayColumn,
  updateSystemDisplayColumn as updateSystemDisplayColumnAction,
  updateUserDisplayColumns as updateUserDisplayColumnsAction
} from "../user/redux/actions";
import {
  loadBuildingData as loadBuildingDataAction,
  loadFloorData as loadFloorDataAction,
  loadReasonData as loadReasonDataAction,
  loadRoomData as loadRoomDataAction,
  loadStepFormList as loadStepFormListAction,
  updateBuildingOnSite as updateBuildingOnSiteAction,
  updateFloorOnSite as updateFloorOnSiteAction,
  updateInstrumentFilter as updateInstrumentFilterAction,
  updateRoomOnSite as updateRoomOnSiteAction
} from "./redux/actions";
import styled from "styled-components";
import { notifyDbMismatch } from "../../utils/helpers/notifyDbMismatch";
import { signOut } from "../../utils/signout";
import { getSortedUniqueList, sortedList } from "../importFile/utils/helpers";
import {
  ACCESS_TOKEN_PATH,
  allTableColumn,
  DEFAULT_COLUMNS,
  DEFAULT_FILTER,
  DEFAULT_IR_PAGE_SIZE,
  DEFAULT_MYLAB_FILTER,
  DEFAULT_SITE_NAME,
  DEFAULT_SITE_TIMEZONE,
  GROUPS_TOKEN_PATH,
  INSTRUMENT_REPOSITORY_ADMIN,
  INSTRUMENT_REPOSITORY_USER,
  INSTRUMENT_REPOSITORY_VIEWER,
  IR_FILE_UPLOAD_STATUS,
  MY_LAB,
  SEARCH_PREFERENCE_LIST,
  SYNC_PROFILE_FIELDS
} from "../../constants";
import {
  CREATE_DIGITAL_LAB_INSTRUMENT_USER_PROFILE,
  GET_IR_BUILDING,
  GET_IR_FLOOR,
  GET_IR_ROOM,
  GET_LOGGED_USER_DETAILS,
  LIST_DIGITAL_LAB_INSTRUMENT_USER_FILTER,
  LIST_IR_BUILDING_MASTER,
  LIST_IR_DISPLAY_COLUMNS,
  LIST_IR_EDITREASON_MASTER,
  LIST_IR_FLOOR_MASTER,
  LIST_IR_GROUP_ADMIN_MAPPINGS,
  LIST_IR_INBOXES,
  LIST_IR_INBOX_BY_USER_ID_AND_CREATE_DATE,
  LIST_IR_LAB_AREAS,
  LIST_IR_ROOM_MASTER,
  LIST_IR_SYSTEM_DISPLAY_COLUMNS,
  LIST_IR_UPLOAD_STATUS_MASTER,
  LIST_SITES,
  LP_GET_LOGGED_USER_DETAILS_USER_FILTERS,
  UPDATE_DIGITAL_LAB_INSTRUMENT_USER_PROFILE_PERSONAL_FIELDS
} from "../../gql/landingapi";
import { getAllData, getData } from "../../utils/helpers/fetching";
import { ON_CREATE_IR_INBOX, ON_UPDATE_IR_INBOX, ON_UPDATE_IR_UPLOAD_STATUS } from "../../gql/landingapi/subscription";
import { Notify } from "@digitallab/grid-common-frontend-components";
import omitDeep from "omit-deep-lodash";
import { find, get, sortBy } from "lodash";
import GenericSubscription from "../../components/shared/GenericSubscription";
import { useHistory, useParams } from "react-router-dom";
import { checkRoleInstrumentRepo } from "../../utils/helpers/checkRoleInstrumentRepo";
import { configureAwsAppSync } from "../../utils/helpers/configureAwsAppSync";
import { CircularProgress, Typography } from "@mui/material";
import { OwcButton } from "@one/react";
import {
  clusterCategoryMappings,
  equipmentCategoryMappings,
  getEquipmentAdministratorMaster,
  getEquipmentNickNameMaster,
  getGroupMaster,
  getLinkedInstance,
  getResponsibleMaster,
  getSops,
  getTagsMaster
} from "./loadInstrumentsInfoService";
import { isValidJson } from "../../utils/helpers/text";
import { loadEquipmentGridColumns } from "../schema/helpers/equipmentGridColumns";

export const FullScreenCentered = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin: 0;
  width: 100vw;
  height: 100vh;, ...syncFields
`;

class SiteError extends Error {
  constructor(message) {
    super(message);
    this.name = "SiteError";
  }
}

const CONDITIONAL_REQUEST_FAILED_ERROR = "GraphQL error: The conditional request failed";
const EMAIL_CHANGED_MESSAGE =
  "Your email has changed, please wait a minute and login again for the change to take effect";

const isSsoAuthenticatedUser = (currentAuthenticatedUser) => {
  const identities = get(currentAuthenticatedUser, `${ACCESS_TOKEN_PATH}.identities`);
  if (Array.isArray(identities)) {
    return identities.length > 0;
  }

  if (typeof identities === "string") {
    try {
      const parsedIdentities = JSON.parse(identities);
      return Array.isArray(parsedIdentities) && parsedIdentities.length > 0;
    } catch (error) {
      return false;
    }
  }

  return false;
};

const normalizeEmail = (email) => (typeof email === "string" ? email.trim().toLowerCase() : "");

export const checkRoleAndGetUserEmail = async (
  required_admin_group = INSTRUMENT_REPOSITORY_ADMIN,
  required_view_group = INSTRUMENT_REPOSITORY_VIEWER,
  required_user_group = INSTRUMENT_REPOSITORY_USER
) => {
  const currentAuthenticatedUser = await Auth.currentAuthenticatedUser();
  const access_groups = get(currentAuthenticatedUser, GROUPS_TOKEN_PATH);
  if (Array.isArray(access_groups)) {
    if (
      access_groups.includes(required_admin_group) ||
      access_groups.includes(required_view_group) ||
      access_groups.includes(required_user_group)
    ) {
      const syncFields = pick(get(currentAuthenticatedUser, ACCESS_TOKEN_PATH), Object.values(SYNC_PROFILE_FIELDS));
      // Amplify sometimes hands back a user whose `attributes` were never populated. The ID
      // token payload carries the same email, so fall back to it rather than reporting an
      // empty address downstream as if the account had no email.
      const email =
        get(currentAuthenticatedUser, ["attributes", "email"]) ||
        get(currentAuthenticatedUser, `${ACCESS_TOKEN_PATH}.email`);
      return {
        email,
        roles: access_groups,
        isSsoUser: isSsoAuthenticatedUser(currentAuthenticatedUser),
        ...syncFields
      };
    } else {
      throw new Error("You do not have permission to access.");
    }
  } else {
    throw new Error("You do not have permission to access.");
  }
};

export const updateProfileFields = async ({ client, email, user, ...syncFields }) => {
  const profileEmail = user?.email || email;
  const syncKeys = Object.keys(SYNC_PROFILE_FIELDS);
  const profileObj = syncKeys.reduce(
    (acc, currKey) => ({
      ...acc,
      [currKey]: syncFields?.[SYNC_PROFILE_FIELDS?.[currKey]] ?? ""
    }),
    {}
  );
  if (!user) {
    return profileObj;
  }
  const shouldUpdate = !isEqual(pick(user, syncKeys), {
    ...profileObj,
    site: profileObj?.site || user.site
  });
  if (!shouldUpdate) return user;
  const jsonObj =
    typeof user?.lastFilter === "string"
      ? isValidJson(user?.lastFilter)
        ? JSON.parse(user?.lastFilter)
        : { lastFavFilter: DEFAULT_FILTER }
      : user?.lastFilter;
  try {
    const {
      data: { updateDigitalLabInstrumentRepositoryUserProfile: updatedUser }
    } = await client.mutate({
      mutation: UPDATE_DIGITAL_LAB_INSTRUMENT_USER_PROFILE_PERSONAL_FIELDS,
      fetchPolicy: "no-cache",
      variables: {
        id: user?.id,
        email: profileEmail,
        lastFilter: JSON.stringify(jsonObj),
        ...profileObj,
        site: profileObj?.site || user?.site
      }
    });
    return updatedUser;
  } catch (error) {
    // The profile record is keyed on email, so a conditional failure here does mean the
    // address moved. Map it at the mutation instead of over the whole load sequence, where
    // any unrelated conditional failure was being reported as an email change.
    if (error?.message?.includes(CONDITIONAL_REQUEST_FAILED_ERROR)) {
      throw new SiteError(EMAIL_CHANGED_MESSAGE);
    }
    throw error;
  }
};

const converLastFavObjStrToArr = (value) => {
  if (!value) {
    return null;
  }
  return typeof value === "string" ? [value] : value;
};

const convertLastFavDateFromDONM = (filterDefinition, type) => {
  if (filterDefinition?.dateOfNextMaintanance === undefined) {
    return filterDefinition[type] || null;
  } else if (
    !filterDefinition[type] &&
    filterDefinition?.dateOfNextMaintanance &&
    Array.isArray(filterDefinition?.dateOfNextMaintanance)
  ) {
    if (type === "dateTo") {
      return filterDefinition?.dateOfNextMaintanance[1] || null;
    }
    return filterDefinition?.dateOfNextMaintanance[0] || null;
  } else if (!filterDefinition[type] && filterDefinition?.dateOfNextMaintanance) {
    if (type === "dateFrom") {
      return filterDefinition?.dateOfNextMaintanance[type] || null;
    }
    return filterDefinition?.dateOfNextMaintanance[type] || null;
  }

  return null;
};

const convertLastFavDateToObj = (filterDefinition) => {
  let dateObj = {
    dateFrom: null,
    dateTo: null,
    filterType: "date",
    type: "inRange"
  };

  if (filterDefinition?.dateOfNextMaintanance === undefined) {
    dateObj = {
      ...dateObj,
      dateFrom: filterDefinition?.dateFrom ? filterDefinition?.dateFrom + " 00:00:01" : null,
      dateTo: filterDefinition?.dateTo ? filterDefinition?.dateTo + " 00:00:01" : null
    };
    return dateObj;
  } else if (filterDefinition?.dateOfNextMaintanance && Array.isArray(filterDefinition?.dateOfNextMaintanance)) {
    let fromDate;
    let toDate;
    if (filterDefinition?.dateOfNextMaintanance[0].search(":") !== -1) {
      fromDate = filterDefinition?.dateOfNextMaintanance[0];
    } else {
      fromDate = filterDefinition?.dateOfNextMaintanance[0] + " 00:00:01";
    }

    if (filterDefinition?.dateOfNextMaintanance[1] && filterDefinition?.dateOfNextMaintanance[1].search(":") !== -1) {
      toDate = filterDefinition?.dateOfNextMaintanance[1];
    } else {
      toDate = filterDefinition?.dateOfNextMaintanance[1]
        ? filterDefinition?.dateOfNextMaintanance[1] + " 00:00:01"
        : null;
    }

    if (fromDate) {
      dateObj = {
        ...dateObj,
        dateFrom: fromDate,
        dateTo: toDate
      };
    } else {
      return null;
    }
    return dateObj;
  }

  return filterDefinition?.dateOfNextMaintanance;
};

export const getLPUserDetailsUserFilter = async ({ client }) => {
  const LP_config = {
    aws_appsync_graphqlEndpoint: process.env.REACT_APP_LP_AWS_GRAPHQL_ENDPOINT
  };

  configureAwsAppSync(LP_config);
  const userInfo = await API.graphql({
    query: LP_GET_LOGGED_USER_DETAILS_USER_FILTERS,
    client
  });

  const resultObj = get(userInfo, "data.getLoggedInUserDetails.items[0]");
  const myLabFilter = find(resultObj?.userFilters?.items, {
    filterName: MY_LAB
  });

  const myLabFilterFilterdefinition =
    typeof myLabFilter?.filterDefinition === "string"
      ? JSON.parse(myLabFilter?.filterDefinition)
      : myLabFilter?.filterDefinition;

  const lpUserID = resultObj?.id;

  if (myLabFilter && myLabFilterFilterdefinition?.inventoryIds?.length > 0) {
    return {
      myLabFilter: {
        ...DEFAULT_MYLAB_FILTER,
        lpUserID,
        filterDefinition: {
          ...DEFAULT_MYLAB_FILTER?.filterDefinition,
          inventoryIds: myLabFilterFilterdefinition?.inventoryIds
        }
      }
    };
  }

  return { myLabFilter: null, lpUserID };
};

/**
 * getting user groups for a user to filter out a table if users is a viewer
 * @param {class} client gql client
 * @param {object} user user object
 * @returns {[groupItems]} filtered out array of groupItems object to which user belongs
 */
const addGroupsToUser = async (client, user) => {
  let allGroups = [];
  let nextToken = null;

  do {
    const rawGroups = await client.query({
      query: LIST_IR_GROUP_ADMIN_MAPPINGS,
      fetchPolicy: "no-cache",
      variables: {
        ...(nextToken && { nextToken })
      }
    });
    const groupsItems = get(rawGroups, "data.listIRGroupAdminMappings.items") || [];
    nextToken = get(rawGroups, "data.listIRGroupAdminMappings.nextToken");

    allGroups = [...allGroups, ...groupsItems];
  } while (nextToken);

  return allGroups.filter((group) => {
    return (
      group.userIds.some((groupUser) => groupUser.userId === user.user) ||
      group.reviewers?.some((groupReviewer) => groupReviewer.userId === user.user)
    );
  });
};

const createUser = async (client, email, profileFieldsOrUpdatedUser) => {
  const result = await client.mutate({
    mutation: CREATE_DIGITAL_LAB_INSTRUMENT_USER_PROFILE,
    fetchPolicy: "no-cache",
    variables: {
      email,
      lastFilter: "",
      ...profileFieldsOrUpdatedUser
    }
  });

  return {
    user: result.data.createDigitalLabInstrumentRepositoryUserProfile
  };
};

const parseLastFiltersPageSize = (user) => {
  if (user.lastFilter) {
    try {
      user.lastFilter =
        typeof user?.lastFilter === "string"
          ? isValidJson(user?.lastFilter)
            ? JSON.parse(user?.lastFilter)
            : { lastFavFilter: DEFAULT_FILTER }
          : user?.lastFilter;
      user.lastFilter = {
        lastFavFilter: {
          ...user.lastFilter?.lastFavFilter,
          filterDefinition: {
            ...user.lastFilter?.lastFavFilter?.filterDefinition,
            site: converLastFavObjStrToArr(user.lastFilter?.lastFavFilter?.filterDefinition?.site),
            group: converLastFavObjStrToArr(user.lastFilter?.lastFavFilter?.filterDefinition?.group),
            manufacturer: converLastFavObjStrToArr(user.lastFilter?.lastFavFilter?.filterDefinition?.manufacturer),
            category: converLastFavObjStrToArr(user.lastFilter?.lastFavFilter?.filterDefinition?.category),
            responsiblePerson: converLastFavObjStrToArr(
              user.lastFilter?.lastFavFilter?.filterDefinition?.responsiblePerson
            ),
            equipmentAdministrator: converLastFavObjStrToArr(
              user.lastFilter?.lastFavFilter?.filterDefinition?.equipmentAdministrator
            ),
            inventoryIds:
              user.lastFilter?.lastFavFilter === MY_LAB
                ? converLastFavObjStrToArr(user.lastFilter?.lastFavFilter?.filterDefinition?.inventoryIds || null)
                : null,
            dateFrom: convertLastFavDateFromDONM(user.lastFilter?.lastFavFilter?.filterDefinition, "dateFrom"),
            dateTo: convertLastFavDateFromDONM(user.lastFilter?.lastFavFilter?.filterDefinition, "dateTo"),
            dateOfNextMaintanance: convertLastFavDateToObj(user.lastFilter?.lastFavFilter?.filterDefinition)
          }
        }
      };
      const dateFrom = user.lastFilter?.lastFavFilter?.filterDefinition?.dateFrom || null;
      const dateTo = user.lastFilter?.lastFavFilter?.filterDefinition?.dateTo || null;
      user.lastFilter.lastFavFilter.filterName =
        user.lastFilter?.lastFavFilter?.filterName === "Predefined filters" ||
        user.lastFilter?.lastFavFilter?.filterName === "None"
          ? DEFAULT_FILTER?.filterName
          : user.lastFilter?.lastFavFilter?.filterName;
      if (!dateFrom) {
        user.lastFilter.lastFavFilter.filterDefinition.dateFrom = DEFAULT_FILTER?.filterDefinition?.dateFrom;
      }
      if (!dateTo) {
        user.lastFilter.lastFavFilter.filterDefinition.dateTo = DEFAULT_FILTER?.filterDefinition?.dateTo;
      }
      user.lastPageSize = JSON.parse(user.lastPageSize);
      if (typeof user.lastPageSize === "string") {
        user.lastPageSize = JSON.parse(user.lastPageSize);
      }
    } catch (error) {
      console.info("error on processing user filters, proceed with filters not assigned");
    }
  }
};

/**
 *
 * @param {*} user user
 */
const parseLastDisplayColumns = (user) => {
  if (user.lastDisplayColumns) {
    try {
      user.lastDisplayColumns = JSON.parse(user.lastDisplayColumns);
    } catch (error) {
      console.warn("error on processing lastDisplayColumns, proceed with not assigned");
    }
  }
};

const parseSearchPreferences = (user) => {
  if (user.searchPreference) {
    try {
      const jsonSearchPrefrenceObj = JSON.parse(user.searchPreference);
      user.searchPreference = sortBy(jsonSearchPrefrenceObj, "title");
      if (user?.searchPreference?.map((item) => item.title)?.includes("Description")) {
        user.searchPreference = SEARCH_PREFERENCE_LIST;
      }
    } catch (error) {
      console.warn("error on processing searchPreference, proceed with not assigned");
    }
  } else {
    user.searchPreference = SEARCH_PREFERENCE_LIST;
  }
};

export const getUserDetails = async ({
  client,
  email,
  isSsoUser = false,
  updateInstrumentFilter,
  loadFilter,
  ...syncFields
}) => {
  const cognitoEmail = email;
  const result = await client.query({
    query: GET_LOGGED_USER_DETAILS,
    fetchPolicy: "no-cache"
  });
  let user = get(result, "data.getLoggedInUserDetails.items[0]");
  const profileKeyEmail = user?.email || cognitoEmail;
  if (!syncFields?.[SYNC_PROFILE_FIELDS.site]) {
    Notify({
      type: "error",
      title: "",
      text: "Your location has been changed. Please check your settings at the Landing Page."
    });
  }

  // Only a genuine mismatch counts: if either side is missing we know nothing about whether
  // the address changed, and treating an absent Cognito email as a change locked users out.
  const normalizedCognitoEmail = normalizeEmail(email);
  const normalizedProfileEmail = normalizeEmail(user?.email);
  if (
    isSsoUser &&
    user &&
    normalizedCognitoEmail &&
    normalizedProfileEmail &&
    normalizedCognitoEmail !== normalizedProfileEmail
  ) {
    throw new SiteError(EMAIL_CHANGED_MESSAGE);
  }

  const profileFieldsOrUpdatedUser = await updateProfileFields({
    client,
    user,
    email: profileKeyEmail,
    ...syncFields
  });
  if (!user) {
    return createUser(client, profileKeyEmail, profileFieldsOrUpdatedUser);
  } else {
    if (!syncFields?.[SYNC_PROFILE_FIELDS.site] && !user.site) {
      throw new SiteError("You do not have site location assigned. Go to the Landing Page to configure this.");
    }
    user.groups = await addGroupsToUser(client, user);
    parseLastFiltersPageSize(user);
    parseLastDisplayColumns(user);
    parseSearchPreferences(user);

    await updateProfileFields({
      client,
      user,
      email: profileKeyEmail,
      ...syncFields
    });
    return { user };
  }
};


export const hasColumnKeyMismatch = (storedColumns) =>
  allTableColumn().some((column) => column.show && !find(storedColumns, { key: column.key }));

export const getFilterConstData = async (data, id) => {
  let filterArr = [];
  data.forEach((item) => {
    var result = allTableColumn().find((obj) => {
      return obj.key === item.key;
    });
    // A stored configuration can reference a column allTableColumn() no longer knows
    // about. Spreading the undefined `find` result pushed a keyless entry, which then
    // threw in convertToSnakeCase(value.key) when the grid built its column defs.
    if (!result) {
      return;
    }
    result = { ...result };
    result.order = item.order;
    result.show = id === "show";
    filterArr.push(result);
  });
  return filterArr;
};

const getUserFilter = async ({ client, query_user_filter }) => {
  const userFilterData = await getAllData({
    query: query_user_filter,
    client,
    dataPath: ["data", "getLoggedInUserDetails"],
    drillData: true
  });

  const userFilters = userFilterData?.items[0]?.userFilters?.items || [];

  userFilters.forEach((item, index, items) => {
    items[index]["filterName"] = item?.filterName === "None" ? "" : item?.filterName;
    if (typeof item["filterDefinition"] === "string") {
      const parsedObj = JSON.parse(item["filterDefinition"]);
      items[index]["filterDefinition"] = {
        ...parsedObj,
        site: converLastFavObjStrToArr(parsedObj.site),
        group: converLastFavObjStrToArr(parsedObj.group),
        manufacturer: converLastFavObjStrToArr(parsedObj.manufacturer),
        category: converLastFavObjStrToArr(parsedObj.category),
        responsiblePerson: converLastFavObjStrToArr(parsedObj?.responsiblePerson),
        equipmentAdministrator: converLastFavObjStrToArr(parsedObj?.equipmentAdministrator),
        dateFrom: convertLastFavDateFromDONM(parsedObj, "dateFrom"),
        dateTo: convertLastFavDateFromDONM(parsedObj, "dateTo"),
        dateOfNextMaintanance: convertLastFavDateToObj(parsedObj),
        inventoryIds: null
      };
    }
  });
  return {
    userFilters
  };
};

export const updateBuilding = async (client, siteId) => {
  const buildings = await getBuildingData(client, siteId);
  return { buildings: buildings?.items || [] };
};

export const updateFloor = async (client, siteId, id) => {
  const floors = await getFloorData(client, siteId, id);
  return { floors: floors?.items || [] };
};

export const updateRoomData = async (client, siteId, id) => {
  const rooms = await getRoomData(client, siteId, id);
  return { rooms: rooms?.items || [] };
};

export const getBuildingData = async (client, siteId) => {
  return await getAllData({
    client,
    query: LIST_IR_BUILDING_MASTER,
    dataPath: ["data", "listBuildingBySiteIdAndBuildingNumber"],
    variables: { limit: 10000, siteId },
    drillData: true
  });
};
export const getFloorData = async (client, siteId, id) => {
  return await getAllData({
    client,
    query: LIST_IR_FLOOR_MASTER,
    dataPath: ["data", "listFloorBySiteIdAndFloorCode"],
    variables: { limit: 10000, siteId, id },
    drillData: true
  });
};
export const getRoomData = async (client, siteId, id) => {
  return await getAllData({
    client,
    query: LIST_IR_ROOM_MASTER,
    dataPath: ["data", "listRoomBySiteIdAndRoomCode"],
    variables: { limit: 50000, siteId, id },
    drillData: true
  });
};

export const getBuilding = async (client, id) => {
  if (isNaN(parseInt(id))) {
    return null;
  }
  return await getData({
    client,
    query: GET_IR_BUILDING,
    variables: { id: parseInt(id) },
    dataPath: ["data", "getIRBuildingMaster"]
  });
};

export const getFloor = async (client, id) => {
  if (isNaN(parseInt(id))) {
    return null;
  }
  return await getData({
    client,
    query: GET_IR_FLOOR,
    variables: { id: parseInt(id) },
    dataPath: ["data", "getIRFloorMaster"]
  });
};

export const getRoom = async (client, id) => {
  if (isNaN(parseInt(id))) {
    return null;
  }
  return await getData({
    client,
    query: GET_IR_ROOM,
    variables: { id: parseInt(id) },
    dataPath: ["data", "getIRRoomMaster"]
  });
};

export const getReasons = async (client) => {
  const result = await getAllData({
    client,
    query: LIST_IR_EDITREASON_MASTER,
    dataPath: ["data", "listIRConfigurableMasters"],
    variables: { limit: 1000 },
    drillData: true
  });
  return {
    listIRConfigurableMasters: {
      items: sortedList(result.items, ["configText"], ["asc"])
    }
  };
};

export const getLabAreas = async (client) => {
  return await getAllData({
    client,
    query: LIST_IR_LAB_AREAS,
    dataPath: ["data", "listLabAreas"],
    variables: { limit: 1000 },
    drillData: true,
    fetchPolicy: "cache-first"
  });
};

export const getAllMasterData = async ({ client, site }) => {
  const siteName = typeof site === "string" ? site : site?.siteName;
  return Promise.all([
    equipmentCategoryMappings(client),
    clusterCategoryMappings(client),
    getGroupMaster(client),
    getResponsibleMaster(client, siteName),
    getTagsMaster(client, siteName),
    getEquipmentAdministratorMaster(client, siteName),
    getEquipmentNickNameMaster(client, siteName),
    getLinkedInstance(client),
    getSops(client),
    getLabAreas(client)
  ]).then((response) => {
    const equipmentCategoryMappingConfigurations = response[0];
    const clusterCategoryMappingConfigurations = response[1];
    const groupMasters = response[2];
    const filterLookups = response[3];
    const tagsMasters = response[4];
    const equipmentAdministrators = response[5];
    const equipmentNickNames = response[6];
    const linkedInstanceMasters = response[7];
    const sopMasters = response[8];
    const labAreas = response[9];

    return {
      listIRCategoryMappingConfigurations: {
        items: equipmentCategoryMappingConfigurations.items
      },
      listIRManufacturerMasters: {
        items: getSortedUniqueList(equipmentCategoryMappingConfigurations.items, "manufacturer")
      },
      listIRVariant: {
        items: getSortedUniqueList(equipmentCategoryMappingConfigurations.items, "variant")
      },
      listIRGroupMasters: {
        items: sortedList(groupMasters.items, ["value"])
      },
      listIRSOPMasters: {
        items: sortedList(sopMasters.items, ["value"])
      },
      listIRequipmentModelConfigurations: {
        items: getSortedUniqueList(equipmentCategoryMappingConfigurations.items, "equipmentModel")
      },
      listIRclusterModelConfigurations: {
        items: getSortedUniqueList(clusterCategoryMappingConfigurations.items, "equipmentModel")
      },
      listIREquipmentCategoryConfigurations: {
        items: getSortedUniqueList(equipmentCategoryMappingConfigurations.items, "equipmentCategory")
      },
      listIRClusterCategoryMappings: {
        items: getSortedUniqueList(clusterCategoryMappingConfigurations.items, "equipmentCategory")
      },
      filterValuesMaster: {
        items: sortedList(filterLookups.items, ["filterValue"])
      },
      listIRTags: {
        items: sortedList(tagsMasters.items, ["filterValue"])
      },
      listIREquipmentAdministrators: {
        items: sortedList(equipmentAdministrators?.items, ["filterValue"])
      },
       listIREquipmentNickNames: {
        items: sortedList(equipmentNickNames?.items, ["filterValue"])
      },
      listLinkedInstanceMasters: {
        items: sortedList(linkedInstanceMasters.items, ["linkedInstanceDisplay"])
      },
      listLabAreas: {
        items: (sortedList(labAreas.items, ["areaName"]) || []).map((item) => item.areaName)
      }
    };
  });
};

export const getUserDisplayColumn = async ({ client, userID }) => {
  const { items: listUserDisplays } = await getAllData({
    client,
    query: LIST_IR_DISPLAY_COLUMNS,
    variables: { userID },
    dataPath: ["data", "listUserDisplays"]
  });

  return listUserDisplays;
};

export const getSystemDisplayColumn = async ({ client }) => {
  const { items: listSystemDisplays } = await getAllData({
    client,
    query: LIST_IR_SYSTEM_DISPLAY_COLUMNS,
    dataPath: ["data", "listSystemDisplays"]
  });

  let systemDisplay = {
    ...listSystemDisplays[0],
    displayDefinition: listSystemDisplays[0]?.displayDefinition
      ? JSON.parse(listSystemDisplays[0]?.displayDefinition)
      : null
  };
  let hiddenColumns = allTableColumn()?.filter((columnObj) => {
    const obj = find(systemDisplay?.displayDefinition?.showColumns, {
      key: columnObj?.key
    });
    return !obj;
  });

  let systemDisplayFinal = [];

  if (systemDisplay.displayDefinition != null) {
    systemDisplay = {
      ...systemDisplay,
      displayDefinition: JSON.stringify({
        ...systemDisplay?.displayDefinition,
        hideColumns: hiddenColumns
      })
    };
    systemDisplayFinal = [systemDisplay];
  }

  return systemDisplayFinal;
};

const LoadInstrumentInfo = ({
  children,
  client,
  loadUserInfo,
  loadActionStatus,
  loadStepFormList,
  updateInstrumentFilter,
  loadFilter,
  loadReasonData,
  loadLastDisplayColumn,
  updateUserDisplayColumns,
  updateSystemDisplayColumn,
  setIsInboxActive,
  setInboxCount
}) => {
  const redirectToTableau = localStorage.getItem("redirectToTableau");
  const history = useHistory();
  if (redirectToTableau === "true") history.push("/tableau-equipments");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const [userObject, setUserObject] = useState({});
  const { InventoryId } = useParams();

  const onCreateCallback = (subscriptionData) => {
    if (userObject.email === subscriptionData?.data?.onCreateIRInbox?.emailId) {
      Notify({
        type: "info",
        icon: "circle_info",
        title: "",
        text: "Export is in progress."
      });
    }
  };
  const onUpdateCallback = async (subscriptionData) => {
    const itemData = subscriptionData?.data?.onUpdateIRInbox;
    if (userObject.email === itemData?.emailId && itemData?.status === "COMPLETED") {
      // Refetch via the GSI-backed query (same source as the /export-csv page)
      // so the badge always reflects the authoritative number of COMPLETED
      // inbox records for this user. This avoids drift between the live badge
      // and the count shown after a page refresh, and naturally dedupes when
      // the same record receives multiple onUpdate events.
      const { items } = await getAllData({
        client,
        query: LIST_IR_INBOX_BY_USER_ID_AND_CREATE_DATE,
        variables: {
          limit: 1000,
          createdById: userObject?.user,
          filter: { status: { eq: "COMPLETED" } }
        },
        dataPath: ["data", "listIRInboxByCreatedByIdAndCreatedDate"],
        drillData: true
      });

      setInboxCount(items.length);
      setIsInboxActive(true);
      Notify({
        type: "success",
        icon: "circle_confirm",
        title: "",
        text: "New export is ready to download."
      });
    } else if (userObject.email === itemData?.emailId && itemData?.status === "FAILED") {
      Notify({
        type: "error",
        icon: "alarm",
        title: "",
        text: "Export failed!"
      });
    }
  };

  //let s3VersionIdArray = useSelector((state) => state?.user?.s3VersionIdArray);
  const onUpdateStatusCallback = (subscriptionData) => {
    const itemData = subscriptionData?.data?.onUpdateIRUploadStatus;

    if (userObject.email === itemData?.email) {
      if (
        itemData?.status === IR_FILE_UPLOAD_STATUS[1].statusKey ||
        itemData?.status === IR_FILE_UPLOAD_STATUS[2].statusKey
      ) {
        Notify({
          type: "success",
          icon: "circle_confirm",
          title: "",
          text:
            itemData?.status === IR_FILE_UPLOAD_STATUS[1].statusKey ? "CSV import started" : "CSV successfully uploaded"
        });
      } else if (itemData?.status === IR_FILE_UPLOAD_STATUS[3].statusKey) {
        switch (itemData?.errorMessage) {
          case "INVALID_FILE":
            Notify({
              type: "error",
              title: "",
              text: "Invalid file format"
            });

            break;
          case "??":
            Notify({
              type: "error",
              title: "",
              text: "File could not be loaded"
            });

            break;
          case "IMPORT_FAILED":
            Notify({
              type: "error",
              title: "",
              text: "CSV import failed"
            });

            break;
          case "IMPORT_PARTIALLYFAILED":
            Notify({
              type: "warning",
              title: "",
              text: "Import of some entries failed"
            });

            break;

          default:
            Notify({
              type: "error",
              title: "",
              text: "File could not be loaded"
            });
            break;
        }
      }
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        // The grid's columns come from the schema, and getUserDetails below reconciles the
        // user's stored column layout against them. Load them before that runs so the
        // reconciliation sees the real column set rather than the legacy fallback.
        await loadEquipmentGridColumns();

        const { email, roles, isSsoUser, ...syncFields } = await checkRoleAndGetUserEmail();
        //TODO: uncomment the below line and comment the above to be tested in eu-west-1
        // const email = "ready4s+admin+dev+eu@protonmail.com";

        const { user } = await getUserDetails({
          client,
          email,
          isSsoUser,
          updateInstrumentFilter,
          loadFilter,
          ...syncFields
        });

        setUserObject(user);

        //TODO: uncomment the below line and comment the above to be tested in eu-west-1
        // const user = {
        //   email: "ready4s+admin+dev+eu@protonmail.com",
        //   familyName: "admin",
        //   givenName: "dev",
        //   id: "e1c62a6d-0e2a-4271-9bbf-0b2cae8b2c5d",
        //   lastFilter: {},
        //   name: "ready4s+admin+dev+eu",
        //   phone: "+41616875923",
        //   site: "Rotkreuz",
        //   status: null,
        //   user: "900c7e94-2b18-455b-b501-056fafb6c9f3"
        // };
        // let UserDisplayColumns = await getDisplayColumn({
        //   client,
        //   query: LIST_SITES,
        //   dataPath: ["data", "listSites"]
        // });

        const userID = user.id;
        const listUserDisplays = await getUserDisplayColumn({ client, userID });
        updateUserDisplayColumns(listUserDisplays);

        const listSystemDisplays = await getSystemDisplayColumn({
          client
        });
        updateSystemDisplayColumn(listSystemDisplays);

        const { items: fetchedSites, error: listSitesError } = await getAllData({
          client,
          query: LIST_SITES,
          dataPath: ["data", "listSites"]
        });
        if (listSitesError) {
          notifyDbMismatch(`listSites AppSync error - ${listSitesError?.message || String(listSitesError)}`);
        }
        let sites = fetchedSites;
        if (!Array.isArray(sites) || sites.length === 0) {
          notifyDbMismatch("listSites returned empty array");
        }
        //TODO: uncomment the below line and comment the above to be tested in eu-west-1
        // let sites = [{ siteName: "Rotkreuz" }, { siteName: "Pleasanton" }];
        sites =
          sites?.length === 0
            ? [
                {
                  siteName: DEFAULT_SITE_NAME,
                  siteTimezone: DEFAULT_SITE_TIMEZONE
                }
              ]
            : sites;
        let key_mismatch = false;
        if (user?.lastDisplayColumns !== null && typeof user?.lastDisplayColumns === "object") {
          user.lastDisplayColumns.displayDefinition =
            typeof user.lastDisplayColumns.displayDefinition === "string"
              ? JSON.parse(user.lastDisplayColumns.displayDefinition)
              : user.lastDisplayColumns.displayDefinition;
          var filter_columns = [
            ...user?.lastDisplayColumns?.displayDefinition?.showColumns,
            ...user?.lastDisplayColumns?.displayDefinition?.hideColumns
          ];

          key_mismatch = hasColumnKeyMismatch(filter_columns);
        }
        if (
          user?.lastDisplayColumns?.displayDefinition?.hideColumns &&
          user?.lastDisplayColumns?.displayDefinition?.showColumns
        ) {
          var showFilterData = await getFilterConstData(
            user.lastDisplayColumns?.displayDefinition?.showColumns,
            "show"
          );
          var hideFilterData = await getFilterConstData(
            user.lastDisplayColumns?.displayDefinition?.hideColumns,
            "hide"
          );
        }

        user.lastDisplayColumns = {
          displayName: user?.lastDisplayColumns?.displayName
            ? user?.lastDisplayColumns?.displayName
            : DEFAULT_COLUMNS?.displayName,
          displayDefinition: {
            showColumns:
              !user.lastDisplayColumns?.displayDefinition?.showColumns || key_mismatch
                ? allTableColumn().filter((item) => item.show)
                : showFilterData,
            hideColumns:
              !user.lastDisplayColumns?.displayDefinition?.hideColumns || key_mismatch
                ? allTableColumn().filter((item) => !item.show)
                : hideFilterData
          }
        };

        const { myLabFilter } = await getLPUserDetailsUserFilter({
          client
        });
        let lastFavFilter = user.lastFilter?.lastFavFilter ? user.lastFilter?.lastFavFilter : DEFAULT_FILTER;

        if (
          myLabFilter &&
          myLabFilter?.filterDefinition?.inventoryIds &&
          myLabFilter?.filterDefinition?.inventoryIds?.length > 0 &&
          lastFavFilter?.filterName === MY_LAB
        ) {
          lastFavFilter = myLabFilter;
        } else if (lastFavFilter?.filterName === MY_LAB) {
          if (typeof InventoryId !== "undefined") {
            lastFavFilter = DEFAULT_FILTER;
          }
        }

        let lastPageSize = user?.lastPageSize;
        if (typeof lastPageSize === "string" && lastPageSize !== "") {
          lastPageSize = JSON.parse(lastPageSize);
        }
        lastPageSize =
          lastPageSize === ""
            ? DEFAULT_IR_PAGE_SIZE
            : lastPageSize?.ir_grid === 5
              ? { ...lastPageSize, ir_grid: 10 }
              : lastPageSize;

        loadLastDisplayColumn(user.lastDisplayColumns);

        const { userFilters } = await getUserFilter({
          client,
          email,
          query_user_filter: LIST_DIGITAL_LAB_INSTRUMENT_USER_FILTER
        });

        loadUserInfo({
          email,
          sites,
          ...user,
          userFilters,
          lastFavFilter,
          lastPageSize,
          myLabFilter,
          roles
        });

        const reasons = await getReasons(client);

        loadReasonData(reasons);
        // get Master datas other than building
        const masterData = await getAllMasterData({
          client,
          site: user?.site
        });
        loadStepFormList(omitDeep(masterData, "__typename"));
        const actionStatus = await checkRoleInstrumentRepo(user?.groups?.length > 0, user?.groups?.length > 0);

        loadActionStatus(actionStatus);

        const inboxVariables = {
          limit: 1000,
          createdById: user?.user,
          filter: { status: { eq: "COMPLETED" } }
        };
        const { items } = await getAllData({
          client,
          query: LIST_IR_INBOX_BY_USER_ID_AND_CREATE_DATE,
          variables: inboxVariables,
          dataPath: ["data", "listIRInboxByCreatedByIdAndCreatedDate"],
          drillData: true
        });

        setInboxCount(items.length);
      } catch (err) {
        console.warn(err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadUserInfo, loadStepFormList, client, updateInstrumentFilter, retry, loadFilter]);
  if (loading)
    return (
      <FullScreenCentered>
        <CircularProgress data-testid="loading" size={80} />
      </FullScreenCentered>
    );
  if (error !== null)
    return (
      <FullScreenCentered>
        <>
          <Typography variant="h6" data-testid="message-error" gutterBottom>
            {error?.message ? error?.message : "Unexpected error has occurred"}
          </Typography>
          {error?.name !== "SiteError" && (
            <OwcButton variant="error" onClick={() => setRetry(retry + 1)} data-testid="try-again-button">
              Try again
            </OwcButton>
          )}
          <OwcButton
            data-testid="signout-button"
            variant="secondary"
            style={{ marginTop: ".7rem" }}
            onClick={() => signOut()}
          >
            Sign out
          </OwcButton>
        </>
      </FullScreenCentered>
    );
  return (
    <>
      {children}
      <GenericSubscription
        query={LIST_IR_INBOXES}
        subscription={ON_CREATE_IR_INBOX}
        onUpdateCallback={onCreateCallback}
        variables={{ limit: 1 }}
      />
      <GenericSubscription
        query={LIST_IR_INBOXES}
        subscription={ON_UPDATE_IR_INBOX}
        onUpdateCallback={onUpdateCallback}
        variables={{ limit: 1 }}
      />

      <GenericSubscription
        query={LIST_IR_UPLOAD_STATUS_MASTER}
        subscription={ON_UPDATE_IR_UPLOAD_STATUS}
        onUpdateCallback={onUpdateStatusCallback}
        variables={{ limit: 1 }}
      />
    </>
  );
};

export default compose(
  connect(null, {
    loadUserInfo: loadUserInfoAction,
    loadStepFormList: loadStepFormListAction,
    updateInstrumentFilter: updateInstrumentFilterAction,
    updateBuildingOnSite: updateBuildingOnSiteAction,
    updateFloorOnSite: updateFloorOnSiteAction,
    updateRoomOnSite: updateRoomOnSiteAction,
    loadBuildingData: loadBuildingDataAction,
    loadFloorData: loadFloorDataAction,
    loadRoomData: loadRoomDataAction,
    loadFilter: loadLastFilter,
    loadActionStatus: loadActionStatusAction,
    loadReasonData: loadReasonDataAction,
    loadLastDisplayColumn: updateLastDisplayColumn,
    updateUserDisplayColumns: updateUserDisplayColumnsAction,
    updateSystemDisplayColumn: updateSystemDisplayColumnAction
  }),
  withApollo
)(LoadInstrumentInfo);
